// «Progetti» a tutta larghezza: di solito uno lavora e gli altri sono fermi. Ogni scheda dice
// lo stato, i lavori del piano dell'ultima sessione, il team con i suoi pops e il grafico per
// giorno della misura scelta (token o spesa) nel periodo scelto.
import { Link } from 'react-router-dom';
import { Pop } from '../../../components/Pop';
import { Segmented } from '../../../components/Segmented';
import { Select } from '../../../components/Select';
import { eur, tokens as fmtTokens } from '../../../lib/format';
import { relative } from '../../../lib/time';
import type { Project, Usage } from '../../../lib/api';
import type { MissionTask, Session } from '../../../types';
import { teamOf } from '../faces';
import type { Metric, Period } from '../prefs';
import { Widget } from '../Widget';
import { Bars } from './Bars';

const WAITING = new Set(['awaiting_human', 'awaiting_clarification', 'suspended', 'blocked']);
const CLOSED = new Set(['completed', 'failed', 'cancelled']);

type Status = { label: string; tone: string; rank: number; note: string };

function statusOf(last: Session | undefined): Status {
  if (!last) return { label: 'Mai avviato', tone: 'neutral', rank: 3, note: 'nessuna sessione' };
  if (WAITING.has(last.state))
    return {
      label: 'Aspetta te',
      tone: 'warn',
      rank: 1,
      note: `fermo ${relative(last.updatedAt)}`,
    };
  if (!CLOSED.has(last.state))
    return { label: 'In lavorazione', tone: 'accent', rank: 0, note: last.title };
  return {
    label: 'Fermo',
    tone: 'neutral',
    rank: 2,
    note: `ultimo lavoro ${relative(last.updatedAt)}`,
  };
}

function PlanBar({ tasks }: { tasks: MissionTask[] | undefined }) {
  const work = (tasks ?? []).filter((t) => t.phase === 'execution');
  if (work.length === 0) return <div className="t-caption faint">Nessun piano in corso.</div>;
  const done = work.filter((t) => t.state === 'done').length;
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <div style={{ display: 'flex', gap: 3 }}>
        {work.map((t) => (
          <span
            key={t.id}
            title={t.title}
            style={{
              flex: 1,
              height: 6,
              borderRadius: 3,
              background:
                t.state === 'done'
                  ? 'var(--ok)'
                  : t.state === 'running'
                    ? 'var(--accent)'
                    : t.state === 'failed'
                      ? 'var(--danger)'
                      : 'var(--border)',
            }}
          />
        ))}
      </div>
      <span className="t-caption dim">
        lavori del piano: {done} di {work.length}
      </span>
    </div>
  );
}

export function Projects({
  projects,
  last,
  tasks,
  usage,
  metric,
  period,
  onPrefs,
}: {
  projects: Project[] | null;
  last: Map<string, Session>;
  tasks: Record<string, MissionTask[]>;
  usage: Usage | null;
  metric: Metric;
  period: Period;
  onPrefs: (p: { metric?: Metric; period?: Period }) => void;
}) {
  const rows = (projects ?? [])
    .map((p) => ({ p, s: last.get(p.id), st: statusOf(last.get(p.id)) }))
    .sort(
      (a, b) => a.st.rank - b.st.rank || (b.s?.updatedAt ?? '').localeCompare(a.s?.updatedAt ?? ''),
    );
  const working = rows.filter((r) => r.st.rank === 0).length;
  const days = usage?.days.slice(-period) ?? [];
  const fmt = metric === 'cost' ? eur : (v: number) => `${fmtTokens(v)} token`;

  return (
    <Widget
      title="Progetti"
      icon="folder"
      count={projects ? `${working} in lavorazione · ${rows.length - working} fermi` : undefined}
      detailsTo="/progetti"
      detailsLabel="Tutti i progetti"
      tint="mint"
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="t-caption dim">Grafico</span>
        <div style={{ width: 200 }}>
          <Select
            label="Misura del grafico"
            value={metric}
            onChange={(v) => onPrefs({ metric: v as Metric })}
            options={[
              { value: 'tokens', label: 'Token al giorno' },
              { value: 'cost', label: 'Spesa al giorno (€)' },
            ]}
            width="100%"
          />
        </div>
        <Segmented
          label="Periodo del grafico"
          value={String(period) as '7' | '14' | '30'}
          items={[
            { value: '7', label: '7 g' },
            { value: '14', label: '14 g' },
            { value: '30', label: '30 g' },
          ]}
          onChange={(v) => onPrefs({ period: Number(v) as Period })}
        />
      </div>
      {projects === null ? (
        <div className="t-caption faint">Leggo i progetti…</div>
      ) : rows.length === 0 ? (
        <div className="t-caption faint">
          Nessun progetto. <Link to="/progetti">Creane uno</Link>.
        </div>
      ) : (
        <div className="dash-projects">
          {rows.slice(0, 8).map(({ p, s, st }) => {
            const series = usage?.byProject[p.id]?.[metric === 'cost' ? 'costEur' : 'tokens'];
            const values = series ? series.slice(-period) : days.map(() => 0);
            const team = teamOf(s ? tasks[s.id] : undefined);
            const live = st.rank === 0;
            return (
              <Link
                key={p.id}
                to={s ? `/s/${s.id}` : '/progetti'}
                className="agent-card dash-project"
                data-live={live || undefined}
              >
                <span style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span className="t-strong trunc" style={{ display: 'block' }}>
                      {p.name}
                    </span>
                    <span className="t-caption dim trunc" style={{ display: 'block' }}>
                      {st.note}
                    </span>
                  </span>
                  <span
                    className="dash-pill"
                    style={{ color: `var(--${st.tone})`, background: `var(--${st.tone}-soft)` }}
                  >
                    {st.label}
                  </span>
                </span>
                <PlanBar tasks={s ? tasks[s.id] : undefined} />
                <span className="dash-chart">
                  <Bars
                    values={values}
                    days={days}
                    height={48}
                    muted={!live}
                    format={fmt}
                    color={live ? 'var(--accent)' : 'var(--dim)'}
                  />
                </span>
                <span style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="t-caption faint">
                    {metric === 'cost' ? 'spesa' : 'token'} al giorno · {period} giorni
                  </span>
                  <span className="t-caption dim">{fmt(values.reduce((a, b) => a + b, 0))}</span>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 28 }}>
                  {team.length ? (
                    <>
                      {team.slice(0, 5).map((m) => (
                        <Pop key={m.name} avatar={m.avatar} size={28} label={m.name} />
                      ))}
                      <span className="t-caption dim">
                        {live ? `${team.length} al lavoro` : `team di ${team.length}`}
                      </span>
                    </>
                  ) : (
                    <span className="t-caption faint">nessun team ancora</span>
                  )}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </Widget>
  );
}
